import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { DateField, EmptyState, StatusChip, type DataTableColumn, type StatusTone } from '@routes-plan/ui';
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { setPageTitle } from '../../utils/pageTitle';
import { useT, type TextKey } from '../../i18n';
import { formatCurrency as companyMoney } from '../../utils/format/currency';
import { ReportFilters, ReportPage, ReportResults, ReportTable } from './components/ReportLayout';

/**
 * Informes → Riesgo por representante (antifraude). Por persona, en las rutas que el usuario puede ver:
 * lo que cobró, sus descuentos, refinanciaciones, borrados, correcciones, exportaciones y señales antifraude,
 * con un puntaje para revisar primero a quien más lo necesita.
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Level = 'low' | 'medium' | 'high';
interface RiskRow {
  user: { id: number; username: string | null; fullName: string | null };
  payments: number;
  collected: number;
  discountTotal: number;
  discountCount: number;
  discountShare: number;
  refinances: number;
  deletedLoans: number;
  deletedClients: number;
  correctionRequests: number;
  exports: number;
  signals: Record<string, number>;
  openAlerts: number;
  score: number;
  level: Level;
  reasons: { key: string; points: number }[];
}

const LEVEL_TONE: Record<Level, StatusTone> = { high: 'danger', medium: 'warning', low: 'success' };

interface RiskTableRow {
  id: number;
  risk: RiskRow;
  rep: string;
  levelText: string;
  reasonsText: string;
  payments: number;
  collectedText: string;
  discountText: string;
  refinances: number;
  deletedText: string;
  correctionRequests: number;
  exports: number;
  signalsText: string;
  openAlerts: number;
}
const SIGNAL_KINDS = ['mock_location', 'emulator', 'daily_limit', 'clock_skew', 'far_payment'];

const addDays = (ymd: string, days: number) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const SalesRepRisk: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('menu.reports.sales-rep-risk');
  const navigate = useNavigate();
  const today = nowOperationalDateString();
  const [from, setFrom] = useState(addDays(today, -29));
  const [to, setTo] = useState(today);
  const [data, setData] = useState<RiskRow[] | null>(null);

  const money = (n: number) => companyMoney(n || 0);
  const reasonText = (r: { key: string }) =>
    r.key.startsWith('signal.')
      ? t(`alerts.kind.${r.key.slice(7)}` as TextKey)
      : t(`salesRepRisk.reason.${r.key}` as TextKey);

  const load = async () => {
    showLoading();
    try {
      const { data: res } = await axios.get('reports/sales-rep-risk', { params: { from, to } });
      setData(res.data || []);
    } catch (error) {
      setData((prev) => prev ?? []);
      showAlert(t('common.loadError'), 'error');
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo<DataTableColumn<RiskTableRow>[]>(
    () => [
      {
        id: 'rep',
        header: t('salesRepRisk.colRep'),
        value: (row) => row.rep,
        sortable: true,
        render: (row) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.rep}
            </Typography>
            {row.risk.user.username && (
              <Typography variant="caption" color="text.secondary">
                {row.risk.user.username}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'level',
        header: t('salesRepRisk.colLevel'),
        value: (row) => row.risk.score,
        csv: (row) => row.levelText,
        sortable: true,
        render: (row) => (
          <StatusChip tone={LEVEL_TONE[row.risk.level]} label={`${t(`salesRepRisk.level.${row.risk.level}` as TextKey)} · ${row.risk.score}`} />
        ),
      },
      { id: 'reasonsText', header: t('salesRepRisk.colReasons'), value: (row) => row.reasonsText, wrap: true },
      { id: 'payments', header: t('salesRepRisk.colPayments'), value: (row) => row.payments, align: 'right', sortable: true },
      { id: 'collected', header: t('salesRepRisk.colCollected'), value: (row) => row.risk.collected, render: (row) => row.collectedText, csv: (row) => row.collectedText, align: 'right', sortable: true },
      { id: 'discount', header: t('salesRepRisk.colDiscounts'), value: (row) => row.risk.discountTotal, render: (row) => row.discountText, csv: (row) => row.discountText, align: 'right', sortable: true },
      { id: 'refinances', header: t('salesRepRisk.colRefinances'), value: (row) => row.refinances, align: 'right', sortable: true },
      { id: 'deletedText', header: t('salesRepRisk.colDeleted'), value: (row) => row.deletedText, align: 'right' },
      { id: 'correctionRequests', header: t('salesRepRisk.colCorrections'), value: (row) => row.correctionRequests, align: 'right', sortable: true },
      { id: 'exports', header: t('salesRepRisk.colExports'), value: (row) => row.exports, align: 'right', sortable: true },
      { id: 'signalsText', header: t('salesRepRisk.colSignals'), value: (row) => row.signalsText, wrap: true },
      {
        id: 'openAlerts',
        header: t('salesRepRisk.colAlerts'),
        value: (row) => row.openAlerts,
        align: 'right',
        sortable: true,
        render: (row) =>
          row.openAlerts > 0 ? (
            <Button size="small" color="warning" onClick={() => navigate('/admin/alerts')}>
              {row.openAlerts}
            </Button>
          ) : (
            0
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const rows: RiskTableRow[] = (data || []).map((r) => ({
    id: r.user.id,
    risk: r,
    rep: r.user.fullName || r.user.username || `#${r.user.id}`,
    levelText: `${t(`salesRepRisk.level.${r.level}` as TextKey)} (${r.score})`,
    reasonsText: r.reasons.length ? r.reasons.map(reasonText).join(' · ') : t('salesRepRisk.noReasons'),
    payments: r.payments,
    collectedText: money(r.collected),
    discountText: r.discountTotal > 0 ? `${money(r.discountTotal)} (${r.discountShare}%)` : '0',
    refinances: r.refinances,
    deletedText: `${r.deletedLoans} / ${r.deletedClients}`,
    correctionRequests: r.correctionRequests,
    exports: r.exports,
    signalsText:
      SIGNAL_KINDS.filter((k) => r.signals[k])
        .map((k) => `${t(`alerts.kind.${k}` as TextKey)}: ${r.signals[k]}`)
        .join(' · ') || '—',
    openAlerts: r.openAlerts,
  }));

  return (
    <ReportPage title={t('menu.reports.sales-rep-risk')} description={t('salesRepRisk.intro')}>
      <ReportFilters onSubmit={() => void load()} submitLabel={t('salesRepRisk.search')} submitButtonProps={{ 'data-tour': 'risk-search' }}>
        <DateField label={t('auditReports.startDate')} data-tour="risk-start" value={from} onChange={(v) => setFrom(v ?? '')} max={minYmd(to, today)} size="small" fullWidth />
        <DateField label={t('auditReports.endDate')} data-tour="risk-end" value={to} onChange={(v) => setTo(v ?? '')} min={from} max={today} size="small" fullWidth />
      </ReportFilters>

      <ReportResults>
        <Box data-tour="risk-table" sx={{ flex: '1 1 0', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <ReportTable<RiskTableRow>
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={data === null}
            initialSort={{ columnId: 'level', direction: 'desc' }}
            exportFileName={t('salesRepRisk.exportFile')}
            tourId="sales-rep-risk"
            ariaLabel={t('menu.reports.sales-rep-risk')}
            emptyState={<EmptyState dense title={t('salesRepRisk.empty')} />}
          />
        </Box>
      </ReportResults>
    </ReportPage>
  );
};

export default SalesRepRisk;
